import { useCallback } from "react";
import clsx from "clsx";
import { Button } from "src/react-migration/components/Button/Button";
import { Link } from "react-router-dom";
import { Tag } from "react-migration/components/Tag/Tag";
import { AddOn } from "../types";

type AddOnPromotionCardProps = AddOn & {
  onPrimaryActionClick: () => void;
  primaryActionText: string;
  primaryActionDisabled?: boolean;
  linkTo: string;
};

export function AddOnPromotionCard({
  title,
  subtitle,
  description,
  logoSrc,
  tags,
  onPrimaryActionClick,
  primaryActionText,
  primaryActionDisabled = false,
  linkTo,
}: AddOnPromotionCardProps) {
  const primaryActionClickHandler = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onPrimaryActionClick();
    },
    [onPrimaryActionClick]
  );

  return (
    <Link
      className={clsx(
        "flex flex-col justify-between rounded-lg border border-blue-grey-200 border-solid overflow-hidden",
        // This monstrosity allows us to underline the learn more link when anything (other than the contact button) is hovered
        "[&:not(:has(.add-on-card-contact-btn:hover)):hover_.add-on-card-learn-more]:underline"
      )}
      to={linkTo}
    >
      <div className={clsx("flex flex-col gap-y-4 w-full overflow-hidden p-4")}>
        <div
          className={clsx(
            "flex flex-col justify-center items-center shrink-0 altas-grow-0 rounded-lg border border-blue-grey-300 w-32 h-16 p-4"
          )}
        >
          <img src={logoSrc} alt={`${title} Logo`} className="w-full" />
        </div>
        <section className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <div className="pt-[3px]">
              <h1 className="inline text-sm font-semibold text-text-base-primary -tracking-wide">{title}</h1>
              {subtitle && <span className="ml-1 font-normal text-3xs text-platform-blue-700">{subtitle}</span>}
            </div>
            <p className="text-xs text-text-base-secondary-a">{description}</p>
          </div>
          <div className="flex flex-row max-w-full gap-4 overflow-x-auto">
            {tags.map((tag, index) => (
              <Tag key={index} tag={tag.text} color={tag.color} size="xs" />
            ))}
          </div>
        </section>
      </div>
      <div className="flex flex-row items-center justify-between p-3 shrink-0 bg-blue-grey-50">
        <span className="leading-none no-underline cursor-pointer select-none add-on-card-learn-more">Learn more</span>
        <Button
          className="add-on-card-contact-btn shrink-0 grow-0 h-9"
          variant="primary"
          size="md"
          onClick={primaryActionClickHandler}
          disabled={primaryActionDisabled}
        >
          {primaryActionText}
        </Button>
      </div>
    </Link>
  );
}
